/** The tabs view's tab bar (plan D1, §1.1): Booking · Guest · Money · Check-in ·
 *  Integration · Activity · Communication, sticky under the page tabs where the spine
 *  sits in the spine view. A second level inside the Booking page tab, so the tabs read
 *  as a segmented strip (a tint marks the open one), not as a second row of underlined
 *  page tabs.
 *
 *  In edit mode a tab carries the same marks as a spine node: its unsaved-change count
 *  and, amber, how many of them the restech's next update will undo — badges for the
 *  eye, words in the tab's accessible name. `1`–`7` (bound by the page) open the tabs.
 *
 *  A WAI-ARIA tab list (MUI `Tabs`: arrow keys move between tabs, Enter / Space opens
 *  one); each tab controls the view's one tab panel. */
import { alpha, Box, Tab, Tabs } from '@mui/material';
import { forwardRef } from 'react';

import type { BookingRecordFacet } from '@/api/bookingRecord';
import { SCROLLER_PADDING_PX } from '@/features/records/page/recordPage';

import { MarkBadges, marksLabel, type StageMarks } from './SpineNav';
import { facetPanelId, facetTabId } from './tabsLayout';

export interface FacetTabsProps {
  facets: { id: BookingRecordFacet; label: string }[];
  active: BookingRecordFacet;
  /** Edit mode: show the marks. */
  editing: boolean;
  marks: Partial<Record<BookingRecordFacet, StageMarks>>;
  /** Display name of the restech ("Ventrata") for the revert mark's words. */
  managedBy: string | null;
  onSelect: (facet: BookingRecordFacet) => void;
}

export const FacetTabs = forwardRef<HTMLDivElement, FacetTabsProps>(function FacetTabs(
  { facets, active, editing, marks, managedBy, onSelect },
  ref,
) {
  return (
    <Box
      ref={ref}
      data-facet-tabs
      sx={{
        position: 'sticky',
        top: -SCROLLER_PADDING_PX,
        zIndex: 3,
        bgcolor: 'background.default',
        mx: -3,
        px: 3,
        py: 1,
        mb: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Tabs
        value={active}
        onChange={(_, v: BookingRecordFacet) => onSelect(v)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Booking facets"
        sx={(t) => ({
          minHeight: 32,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-flexContainer': { gap: 0.5 },
          '& .MuiTab-root': {
            minHeight: 32,
            minWidth: 0,
            px: 1.25,
            py: 0.5,
            borderRadius: 1,
            textTransform: 'none',
            fontSize: t.typography.body2.fontSize,
            fontWeight: 600,
            color: 'text.secondary',
            transition: t.transitions.create(['background-color', 'color'], { duration: 150 }),
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            '&:hover': { color: 'primary.main', bgcolor: alpha(t.palette.primary.main, 0.04) },
            '&.Mui-selected': { color: 'primary.main', bgcolor: alpha(t.palette.primary.main, 0.08) },
            '&.Mui-focusVisible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: -2 },
          },
        })}
      >
        {facets.map((f) => {
          const m = editing ? marks[f.id] : undefined;
          const extra = marksLabel(m, managedBy);
          return (
            <Tab
              key={f.id}
              value={f.id}
              id={facetTabId(f.id)}
              aria-controls={facetPanelId(f.id)}
              {...(extra ? { 'aria-label': `${f.label}. ${extra}` } : {})}
              data-facet={f.id}
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
                  {f.label}
                  <MarkBadges marks={m} managedBy={managedBy} />
                </Box>
              }
            />
          );
        })}
      </Tabs>
    </Box>
  );
});
